import { useEffect, useState } from 'react';
import type { FeedbackApi, FeedbackReason } from '@/api/feedback';

/**
 * Fetch the feedback reason chips once per (api, language) tuple. Silently
 * degrades to an empty list on error so the two-step feedback flow never
 * blocks: the user can still submit a "no-reason down" via the initial
 * PUT even when the reasons endpoint is down.
 */
export function useFeedbackReasons(
  api: FeedbackApi,
  language: string,
): { reasons: FeedbackReason[] } {
  const [reasons, setReasons] = useState<FeedbackReason[]>([]);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();

    api
      .fetchReasons(language, { signal: ctrl.signal })
      .then((list) => {
        if (cancelled) return;
        setReasons(list);
      })
      .catch(() => {
        if (cancelled) return;
        setReasons([]);
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [api, language]);

  return { reasons };
}
